import { useCallback, useState } from 'react';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';

/** localStorage keys are per surface so the offers list and builder remember independently */
export type CardSizeStorageKey =
  | 'tradeOffers.cardSize'
  | 'offerBuilder.cardSize'
  | 'offerBuilder.focusCardSize'
  | 'search.cardSize';

const isCardSize = (value: string | null): value is CardSize =>
  value === 'S' || value === 'M' || value === 'L';

/**
 * Persisted card-art size, defaulting to M (design-pass decision 3) unless a
 * surface names another default (the builder's focus grid opens at S).
 *
 * `allowed` narrows what a surface will accept: Search offers only M/L, so an 'S'
 * left in storage by another surface would otherwise select nothing.
 */
export const useCardSizePreference = (
  storageKey: CardSizeStorageKey,
  allowed?: CardSize[],
  defaultSize: CardSize = 'M'
): [CardSize, (size: CardSize) => void] => {
  const [size, setSize] = useState<CardSize>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!isCardSize(stored)) return defaultSize;
      return !allowed || allowed.includes(stored) ? stored : defaultSize;
    } catch {
      return defaultSize;
    }
  });

  const update = useCallback(
    (next: CardSize) => {
      setSize(next);
      try {
        localStorage.setItem(storageKey, next);
      } catch {
        // localStorage unavailable — keep the in-memory value
      }
    },
    [storageKey]
  );

  return [size, update];
};

export default useCardSizePreference;
